'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import FadeIn from '../ui/FadeIn';
import { Download, Users, ShieldCheck, Zap, Database, ArrowRight, Table as TableIcon, Search, Sparkles, MessageSquare, X, ChevronDown, SendHorizontal, Clock, Eye, EyeOff } from 'lucide-react';
import TextReveal from '../ui/TextReveal';

// --- M3 Sub-Component: Mock Spreadsheet ---
const MockTable = () => {
    const rows = [
        { name: "John Doe", status: "Synced", last: "2m ago", group: "Sales Team" },
        { name: "Sarah Smith", status: "Exported", last: "1h ago", group: "Design Dept" },
        { name: "Michael Ross", status: "Synced", last: "5m ago", group: "Sales Team" },
        { name: "Jessica Day", status: "Pending", last: "10m ago", group: "General" },
        { name: "Harvey Specter", status: "Synced", last: "1m ago", group: "VIP" },
    ];

    const container = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const item = {
        hidden: { opacity: 0, x: -10 },
        visible: { opacity: 1, x: 0, transition: { ease: [0.2, 0, 0, 1] as [number, number, number, number] } }
    };

    return (
        <motion.div
            variants={container}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="w-full bg-[var(--color-md-surface)] rounded-2xl border border-[var(--color-md-outline)] overflow-hidden shadow-cinematic-2"
        >
            <div className="bg-[var(--color-md-surface-variant)] px-5 py-3 border-b border-black/5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <TableIcon className="w-4 h-4 text-[var(--color-md-primary)]" />
                    <span className="text-[11px] font-black text-[var(--color-md-on-surface-variant)] uppercase tracking-widest">Leads_Export_Premium.csv</span>
                </div>
                <div className="flex gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-[var(--color-md-outline)]/30" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[var(--color-md-outline)]/30" />
                </div>
            </div>
            <div className="p-0 overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-[var(--color-md-surface-variant)] border-b border-[var(--color-md-outline)]">
                            <th className="px-6 py-3 text-[10px] font-black text-[var(--color-md-on-surface-variant)] uppercase tracking-[0.2em]">Contact</th>
                            <th className="px-6 py-3 text-[10px] font-black text-[var(--color-md-on-surface-variant)] uppercase tracking-[0.2em]">Status</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--color-md-outline)]">
                        {rows.slice(0, 4).map((row, i) => (
                            <motion.tr key={i} variants={item} className="hover:bg-[var(--color-md-surface-variant)]/50 transition-colors group/row">
                                <td className="px-6 py-4 text-xs font-bold text-[var(--color-md-on-surface)]">{row.name}</td>
                                <td className="px-6 py-4">
                                    <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-tighter ring-1 ring-inset ${row.status === 'Synced' ? 'bg-[var(--color-md-primary)]/10 text-[var(--color-md-on-primary-container)] dark:text-[var(--color-md-on-primary-container)] ring-[var(--color-md-primary)]/30' :
                                        row.status === 'Exported' ? 'bg-[var(--color-md-secondary)]/10 text-[var(--color-md-on-surface-variant)] ring-[var(--color-md-outline)]' :
                                            'bg-amber-500/10 text-amber-700 dark:text-amber-300 ring-amber-400/30'
                                        }`}>
                                        {row.status}
                                    </span>
                                </td>
                            </motion.tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="p-4 bg-[var(--color-md-surface-variant)] border-t border-[var(--color-md-outline)] flex justify-end">
                <div className="flex items-center gap-2 px-4 py-2 bg-[var(--color-md-primary)] text-[var(--color-md-on-primary)] text-[10px] font-black rounded-full shadow-lg hover:scale-105 transition-spring cursor-pointer">
                    <Download className="w-3.5 h-3.5" />
                    DOWNLOAD ASSETS
                </div>
            </div>
        </motion.div>
    );
};


const CRMStatusWidget = () => {
    const container = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const item = {
        hidden: { opacity: 0, x: -10 },
        visible: { opacity: 1, x: 0, transition: { ease: [0.2, 0, 0, 1] as [number, number, number, number] } }
    };

    return (
        <motion.div
            variants={container}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="w-full max-w-[280px] space-y-3"
        >
            {[
                { label: "New Lead", color: "bg-blue-500" },
                { label: "Hot", color: "bg-red-500" },
                { label: "Follow Up", color: "bg-amber-500" }
            ].map((tag, i) => (
                <motion.div key={i} variants={item} className="flex items-center justify-between p-3 bg-[var(--color-md-surface)] rounded-2xl border border-[var(--color-md-outline)] shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${tag.color}`} />
                        <span className="text-xs font-bold text-[var(--color-md-on-surface)]">{tag.label}</span>
                    </div>
                    <div className="flex gap-1">
                        <div className="w-1 h-1 rounded-full bg-[var(--color-md-outline)]/30" />
                        <div className="w-1 h-1 rounded-full bg-[var(--color-md-outline)]/30" />
                    </div>
                </motion.div>
            ))}
        </motion.div>
    );
};


const SearchWidget = () => {
    const container = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const item = {
        hidden: { opacity: 0, scale: 0.95 },
        visible: { opacity: 1, scale: 1, transition: { ease: [0.2, 0, 0, 1] as [number, number, number, number] } }
    };

    return (
        <motion.div
            variants={container}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="w-full max-w-[280px] bg-[var(--color-md-surface)] rounded-2xl border border-[var(--color-md-outline)] p-4 shadow-cinematic-2"
        >
            <div className="flex items-center gap-2 px-3 py-2 bg-[var(--color-md-surface-variant)] rounded-full mb-4">
                <Search className="w-3.5 h-3.5 text-[var(--color-md-on-surface-variant)]" />
                <div className="h-3 w-20 bg-[var(--color-md-outline)]/30 rounded-full" />
            </div>
            <div className="space-y-2">
                {[1, 2].map(i => (
                    <motion.div key={i} variants={item} className="flex items-center gap-3 p-2 hover:bg-[var(--color-md-surface-variant)] rounded-xl transition-colors">
                        <div className="w-8 h-8 rounded-full bg-[var(--color-md-surface-variant)]" />
                        <div className="space-y-1">
                            <div className="h-2 w-16 bg-[var(--color-md-outline)]/40 rounded-full" />
                            <div className="h-1.5 w-10 bg-[var(--color-md-outline)]/20 rounded-full" />
                        </div>
                    </motion.div>
                ))}
            </div>
        </motion.div>
    );
};


const UnsavedChatWidget = () => (
    <div className="w-full max-w-[300px] bg-[var(--color-md-surface)] rounded-2xl border border-[var(--color-md-outline)] shadow-cinematic-2 overflow-hidden">
        {/* Header */}
        <div className="p-5 border-b border-[var(--color-md-outline)]">
            <div className="flex items-center justify-between mb-3">
                <div className="w-9 h-9 rounded-xl bg-[var(--color-md-surface-variant)] flex items-center justify-center border border-[var(--color-md-outline)]">
                    <MessageSquare className="w-4 h-4 text-[var(--color-md-on-surface-variant)]" />
                </div>
                <X className="w-4 h-4 text-[var(--color-md-on-surface-variant)] opacity-50" />
            </div>
            <h4 className="text-lg font-black text-[var(--color-md-on-surface)] tracking-tight">New Chat</h4>
            <p className="text-[11px] text-[var(--color-md-on-surface-variant)] opacity-70">Direct message without saving contact</p>
        </div>

        {/* Phone Input */}
        <div className="p-5">
            <div className="text-[10px] font-bold text-[var(--color-md-on-surface-variant)] uppercase tracking-wider mb-2">Phone Number</div>
            <div className="flex gap-2 mb-4">
                <div className="flex items-center gap-1.5 px-3 py-2.5 bg-[var(--color-md-surface-variant)] rounded-xl border border-[var(--color-md-outline)] text-xs">
                    <span className="text-[10px]">🇮🇳</span>
                    <span className="font-bold text-[var(--color-md-on-surface)]">+91</span>
                    <ChevronDown className="w-3 h-3 text-[var(--color-md-on-surface-variant)]" />
                </div>
                <div className="flex-1 px-4 py-2.5 bg-[var(--color-md-surface-variant)] rounded-xl border border-[var(--color-md-outline)]">
                    <span className="text-xs text-[var(--color-md-on-surface-variant)] opacity-50">Mobile Number</span>
                </div>
            </div>
            <div className="w-full py-3 bg-[var(--color-md-surface-variant)] rounded-xl border border-[var(--color-md-outline)] flex items-center justify-center gap-2 text-[var(--color-md-on-surface-variant)] opacity-50">
                <SendHorizontal className="w-3 h-3" />
                <span className="text-xs font-bold">Open Chat</span>
            </div>
        </div>

        {/* Recent Numbers */}
        <div className="px-5 pb-5">
            <div className="flex items-center gap-2 text-[10px] font-bold text-[var(--color-md-on-surface-variant)] uppercase tracking-wider mb-3">
                <Clock className="w-3 h-3 opacity-50" />
                RECENT NUMBERS
            </div>
            <div className="text-center py-3 text-xs text-[var(--color-md-on-surface-variant)] opacity-40 italic">
                No recent chats
            </div>
        </div>
    </div>
);

const PrivacyBlurWidget = () => {
    const [isBlurred, setIsBlurred] = useState(true);

    useEffect(() => {
        const interval = setInterval(() => {
            setIsBlurred(prev => !prev);
        }, 2500);
        return () => clearInterval(interval);
    }, []);

    const container = {
        hidden: { opacity: 0 },
        visible: { opacity: 1 }
    };

    return (
        <motion.div
            variants={container}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="w-full max-w-[340px] bg-[var(--color-md-surface)] rounded-2xl border border-[var(--color-md-outline)] shadow-cinematic-2 overflow-hidden flex flex-col"
        >
            <div className="p-4 bg-[var(--color-md-surface-variant)] border-b border-[var(--color-md-outline)] flex items-center justify-between">
                <div className="flex items-center gap-2">
                    {isBlurred ? <EyeOff className="w-4 h-4 text-[var(--color-md-primary)]" /> : <Eye className="w-4 h-4 text-[var(--md-sys-color-error)]" />}
                    <span className="text-[10px] font-black uppercase tracking-widest text-[var(--color-md-on-surface)]">
                        {isBlurred ? "Safe Mode" : "Exposed"}
                    </span>
                </div>
            </div>

            <div className="p-5 space-y-4">
                <div className={`flex items-center justify-between p-3.5 rounded-[14px] transition-all duration-500 ${isBlurred ? 'bg-[var(--color-md-primary-container)]/30 shadow-[inset_0_1px_1px_white,0_2px_8px_rgba(0,0,0,0.02)] ring-1 ring-[var(--color-md-primary)]/10' : 'bg-[var(--color-md-surface-variant)] border border-[var(--color-md-outline)]'}`}>
                    <div className="flex items-center gap-3.5">
                        <div className="w-9 h-9 rounded-full bg-[var(--color-md-primary)]/15 flex items-center justify-center text-[var(--color-md-primary)] font-bold text-[13px]">A</div>
                        <div className="flex flex-col">
                            <span className="text-[14px] font-bold text-[var(--color-md-on-surface)] leading-tight">Alice</span>
                            <span className="text-[12px] font-mono text-[var(--color-md-on-surface-variant)] mt-0.5">
                                +91 <span className={`transition-all duration-300 ${isBlurred ? 'blur-[4px] opacity-40' : ''}`}>98765</span> 4321
                            </span>
                        </div>
                    </div>
                </div>

                <div className={`flex items-center justify-between p-3.5 rounded-[14px] transition-all duration-500 ${isBlurred ? 'bg-[var(--color-md-primary-container)]/30 shadow-[inset_0_1px_1px_white,0_2px_8px_rgba(0,0,0,0.02)] ring-1 ring-[var(--color-md-primary)]/10' : 'bg-[var(--color-md-surface-variant)] border border-[var(--color-md-outline)]'}`}>
                    <div className="flex items-center gap-3.5">
                        <div className="w-9 h-9 rounded-full bg-[var(--color-md-tertiary)]/15 flex items-center justify-center text-[var(--color-md-tertiary)] font-bold text-[13px]">B</div>
                        <div className="flex flex-col">
                            <span className="text-[14px] font-bold text-[var(--color-md-on-surface)] leading-tight">Bob</span>
                            <span className="text-[12px] font-mono text-[var(--color-md-on-surface-variant)] mt-0.5">
                                +44 <span className={`transition-all duration-300 ${isBlurred ? 'blur-[4px] opacity-40' : ''}`}>79123</span> 4567
                            </span>
                        </div>
                    </div>
                </div>

                {/* Keyboard Shortcut Indicator */}
                <div className="flex flex-col items-center justify-center pt-6 pb-2 w-full">
                    <div className="flex justify-center gap-4">
                        <div className={`
                            relative flex flex-col items-center justify-center 
                            w-16 h-[54px] rounded-[10px] 
                            transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]
                            border border-[var(--color-md-outline)]
                            ${isBlurred
                                ? 'bg-[var(--color-md-primary)]/10 text-[var(--color-md-primary)] border-[var(--color-md-primary)]/30 scale-95 shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] translate-y-[2px]'
                                : 'bg-[var(--color-md-surface)] text-[var(--color-md-on-surface)] shadow-[0_3px_0_var(--color-md-outline),0_6px_10px_rgba(0,0,0,0.05),inset_0_1px_0_rgba(255,255,255,0.1)] -translate-y-[1px]'
                            }
                        `}>
                            <span className="text-[12px] font-black leading-none font-mono opacity-90 mb-0.5">⌥ / Alt</span>
                            <span className="text-[7.5px] font-bold uppercase tracking-widest opacity-40 mt-1.5">Toggle</span>
                        </div>

                        <div className={`
                            relative flex flex-col items-center justify-center 
                            w-[54px] h-[54px] rounded-[10px] 
                            transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] delay-[50ms]
                            border border-[var(--color-md-outline)]
                            ${isBlurred
                                ? 'bg-[var(--color-md-primary)]/10 text-[var(--color-md-primary)] border-[var(--color-md-primary)]/30 scale-95 shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] translate-y-[2px]'
                                : 'bg-[var(--color-md-surface)] text-[var(--color-md-on-surface)] shadow-[0_3px_0_var(--color-md-outline),0_6px_10px_rgba(0,0,0,0.05),inset_0_1px_0_rgba(255,255,255,0.1)] -translate-y-[1px]'
                            }
                        `}>
                            <span className="text-[15px] font-black leading-none font-sans opacity-90 mb-0.5">⇧</span>
                            <span className="text-[7.5px] font-bold uppercase tracking-widest opacity-40 mt-1">Shift</span>
                        </div>

                        <div className={`
                            relative flex flex-col items-center justify-center 
                            w-[54px] h-[54px] rounded-[10px] 
                            transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] delay-[100ms]
                            border border-[var(--color-md-outline)]
                            ${isBlurred
                                ? 'bg-[var(--color-md-primary)]/10 text-[var(--color-md-primary)] border-[var(--color-md-primary)]/30 scale-95 shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] translate-y-[2px]'
                                : 'bg-[var(--color-md-surface)] text-[var(--color-md-on-surface)] shadow-[0_3px_0_var(--color-md-outline),0_6px_10px_rgba(0,0,0,0.05),inset_0_1px_0_rgba(255,255,255,0.1)] -translate-y-[1px]'
                            }
                        `}>
                            <span className="text-[14px] font-black leading-none font-mono opacity-90 mb-0.5">P</span>
                            <span className="text-[7.5px] font-bold uppercase tracking-widest opacity-40 mt-1.5">Protect</span>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default function Gallery() {
    return (
        <section id="features" className="section-padding bg-[var(--color-md-surface-variant)] relative overflow-hidden">
            <div className="container mx-auto px-6 relative z-10">
                <FadeIn className="text-center mb-24">
                    <TextReveal
                        as="h2"
                        text="WhatsApp contact exporter built for scale"
                        className="text-4xl md:text-6xl font-black text-[var(--color-md-on-surface)] tracking-tight leading-tight mb-8 justify-center"
                    />
                    <p className="text-[var(--color-md-on-surface-variant)] text-xl font-medium max-w-3xl mx-auto">
                        Engineered for speed. Built for scale. Secured by default.
                    </p>
                </FadeIn>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12">
                    {/* Hero Card: Local Extraction */}
                    <FadeIn delay={0.15} className="md:col-span-8">
                        <motion.div
                            whileHover={{ scale: 1.01, y: -5 }}
                            transition={{ type: "spring", stiffness: 300, damping: 20 }}
                            className="bg-[var(--color-md-surface)] border border-[var(--color-md-outline)] shadow-cinematic-1 hover:shadow-cinematic-3 transition-all duration-700 overflow-hidden rounded-[32px] h-full group tilt-card"
                        >
                            <div className="card-oxygen">
                                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[var(--color-md-primary)]/10 text-[var(--color-md-primary)] border border-[var(--color-md-primary)]/20 text-xs font-bold mb-8">
                                    <Database className="w-3.5 h-3.5" />
                                    Infinite extraction
                                </div>
                                <h3 className="text-4xl md:text-6xl font-black text-[var(--color-md-on-surface)] tracking-tighter mb-6">
                                    Instant <br /> Local Sync.
                                </h3>
                                <p className="text-lg text-[var(--color-md-on-surface-variant)] leading-relaxed max-w-lg mb-12">
                                    Zero servers. Zero delays. WaVault extracts data directly from your browser memory into a structured vault.
                                </p>

                                <div className="relative mt-8 group-hover:scale-[1.03] group-hover:-translate-y-2 transition-spring overflow-hidden rounded-2xl">
                                    <div className="absolute inset-x-0 -bottom-12 h-24 bg-gradient-to-t from-[var(--color-md-surface-variant)] via-[var(--color-md-surface-variant)]/80 to-transparent z-10 pointer-events-none" />
                                    <MockTable />
                                </div>
                            </div>
                        </motion.div>
                    </FadeIn>

                    {/* Compact Feature: Security */}
                    <FadeIn delay={0.3} className="md:col-span-4">
                        <motion.div
                            whileHover={{ scale: 1.02, y: -5 }}
                            transition={{ type: "spring", stiffness: 300, damping: 20 }}
                            className="h-full text-[var(--color-md-on-surface)] flex flex-col justify-between overflow-hidden rounded-[32px] shadow-cinematic-3 bg-[var(--color-md-surface)] border border-[var(--color-md-outline-variant)] relative group"
                        >
                            <div className="card-oxygen relative z-10">
                                <div className="w-14 h-14 m3-icon-tonal mb-8 glow-md">
                                    <ShieldCheck className="w-8 h-8" />
                                </div>
                                <h3 className="text-4xl font-black mb-6 tracking-tight leading-none text-[var(--color-md-on-surface)]">
                                    Zero-Knowledge <br /> Security.
                                </h3>
                                <p className="text-[var(--color-md-on-surface-variant)] text-base leading-relaxed">
                                    We never see your data. Your vault stays local, encrypted with AES-256 by hardware-secured keychain.
                                </p>
                            </div>

                            <div className="card-oxygen pt-0 relative z-10">
                                <div className="p-6 rounded-3xl bg-[var(--color-md-surface-variant)] border border-[var(--color-md-outline-variant)] group-hover:border-[var(--color-md-primary)]/30 transition-colors duration-500">
                                    <div className="flex items-center justify-between mb-6">
                                        <span className="text-[10px] font-bold text-[var(--color-md-primary)] tracking-wide uppercase">Hardware cryptography</span>
                                        <div className="flex gap-1.5">
                                            {[1, 2, 3].map(i => <div key={i} className="w-1.5 h-1.5 rounded-full bg-[var(--color-md-primary)] animate-pulse" style={{ animationDelay: `${i * 0.2}s` }} />)}
                                        </div>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="h-2 w-full bg-[var(--color-md-outline-variant)]/30 rounded-full overflow-hidden">
                                            <div className="h-full w-full bg-brand-gradient animate-shimmer opacity-80" />
                                        </div>
                                        <div className="flex justify-between text-[9px] font-black font-mono text-[var(--color-md-on-surface-variant)] tracking-tighter">
                                            <span>RSA: 4096-BIT</span>
                                            <span className="text-[var(--color-md-primary)]">SECURE</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </FadeIn>

                    {/* Link Card: Group Extraction */}
                    <FadeIn delay={0.45} className="md:col-span-4">
                        <motion.div
                            whileHover={{ scale: 1.02, y: -5 }}
                            transition={{ type: "spring", stiffness: 300, damping: 20 }}
                            className="group flex flex-col justify-between min-h-[400px] border border-[var(--color-md-outline)] hover:border-[var(--color-md-primary)]/40 shadow-cinematic-1 hover:shadow-cinematic-2 transition-spring rounded-[32px] bg-[var(--color-md-surface)] h-full relative overflow-hidden"
                        >
                            <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-[var(--color-md-primary)]/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                            <div className="card-oxygen relative z-10">
                                <div className="w-14 h-14 m3-icon-secondary mb-10 transition-all duration-500">
                                    <Users className="w-7 h-7" />
                                </div>
                                <h3 className="text-4xl font-black text-[var(--color-md-on-surface)] mb-4 tracking-tighter">Bulk Scale.</h3>
                                <p className="text-base text-[var(--color-md-on-surface-variant)] leading-relaxed">
                                    Extract massive participant lists from any group instantly. The professional tool for event management and community sales.
                                </p>
                            </div>
                            <Link href="/#pricing" className="card-oxygen pt-0 flex items-center gap-2 text-[var(--color-md-primary)] font-bold text-sm group/btn relative z-10 cursor-pointer">
                                Pro featured <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-2 transition-spring" />
                            </Link>
                        </motion.div>
                    </FadeIn>

                    {/* Stats Card */}
                    <FadeIn delay={0.6} className="md:col-span-8">
                        <motion.div
                            whileHover={{ scale: 1.01, y: -5 }}
                            transition={{ type: "spring", stiffness: 300, damping: 20 }}
                            className="overflow-hidden flex flex-col md:flex-row min-h-[430px] rounded-[32px] bg-[var(--color-md-surface)] border border-[var(--color-md-outline)] shadow-cinematic-1 group hover:shadow-cinematic-3 transition-spring h-full"
                        >
                            <div className="flex-1 card-oxygen flex flex-col justify-center">
                                <div className="flex items-center gap-3 mb-10">
                                    <div className="w-11 h-11 m3-icon-tonal flex-shrink-0">
                                        <Zap className="w-5 h-5 fill-current" />
                                    </div>
                                    <span className="text-xs font-bold text-[var(--color-md-primary)] opacity-70">Industry standard</span>
                                </div>
                                <h3 className="text-4xl md:text-6xl font-black text-[var(--color-md-on-surface)] mb-8 tracking-tighter leading-none">
                                    Absolute <br /> Reliability.
                                </h3>
                                <p className="text-[var(--color-md-on-surface-variant)] text-lg leading-relaxed max-w-sm">
                                    Trusted by power users worldwide. Engineered for high-volume exports with proven reliability.
                                </p>
                            </div>
                            <div className="flex-1 bg-[var(--color-md-surface-variant)]/50 m-2 rounded-[32px] border border-[var(--color-md-outline)] p-12 flex items-center justify-center relative overflow-hidden noise-bg">
                                <div className="relative z-10 grid grid-cols-1 gap-12 w-full max-w-[200px]">
                                    <div className="text-center group-hover:scale-110 transition-transform duration-700">
                                        <div className="text-5xl font-black text-[var(--color-md-on-surface)] tracking-tighter leading-none mb-2">High Volume</div>
                                        <div className="text-xs font-semibold text-[var(--color-md-on-surface-variant)]">Reliability standard</div>
                                    </div>
                                    <div className="text-center group-hover:scale-110 transition-transform duration-700 delay-75">
                                        <div className="text-5xl font-black text-[var(--color-md-on-surface)] tracking-tighter leading-none mb-2">Local First</div>
                                        <div className="text-xs font-semibold text-[var(--color-md-on-surface-variant)]">No server uploads</div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </FadeIn>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 mt-8 lg:mt-12">
                    {/* CRM Status Card */}
                    <FadeIn delay={0.75} className="md:col-span-6">
                        <motion.div
                            whileHover={{ scale: 1.02, y: -5 }}
                            transition={{ type: "spring", stiffness: 300, damping: 20 }}
                            className="group h-full overflow-hidden flex flex-col rounded-[24px] bg-[var(--color-md-surface)] border border-[var(--color-md-outline)] shadow-cinematic-1 hover:shadow-cinematic-2 transition-spring"
                        >
                            <div className="card-oxygen">
                                <div className="w-14 h-14 m3-icon-tertiary mb-8">
                                    <Sparkles className="w-7 h-7" />
                                </div>
                                <h3 className="text-3xl font-black text-[var(--color-md-on-surface)] mb-4 tracking-tighter">Smart CRM.</h3>
                                <p className="text-base text-[var(--color-md-on-surface-variant)] leading-relaxed">
                                    Track lead progress directly within WhatsApp. The professional way to manage your pipeline.
                                </p>
                            </div>
                            <div className="flex-grow flex items-center justify-center p-6 md:p-12 bg-[var(--color-md-surface-variant)]/50 m-2 rounded-[24px] border border-[var(--color-md-outline)] group-hover:bg-[var(--color-md-surface)] transition-colors duration-500">
                                <CRMStatusWidget />
                            </div>
                        </motion.div>
                    </FadeIn>

                    {/* Unsaved Chat Card */}
                    <FadeIn delay={0.9} className="md:col-span-6">
                        <motion.div
                            whileHover={{ scale: 1.02, y: -5 }}
                            transition={{ type: "spring", stiffness: 300, damping: 20 }}
                            className="group h-full overflow-hidden flex flex-col rounded-[24px] bg-[var(--color-md-surface)] border border-[var(--color-md-outline)] shadow-cinematic-1 hover:shadow-cinematic-2 transition-spring"
                        >
                            <div className="card-oxygen">
                                <div className="w-14 h-14 m3-icon-tonal mb-8">
                                    <MessageSquare className="w-7 h-7" />
                                </div>
                                <h3 className="text-3xl font-black text-[var(--color-md-on-surface)] mb-4 tracking-tighter">Quick Chat.</h3>
                                <p className="text-base text-[var(--color-md-on-surface-variant)] leading-relaxed">
                                    Initiate chats without saving contacts. Speed up your workflow with one-click communication.
                                </p>
                            </div>
                            <div className="flex-grow flex items-center justify-center p-6 md:p-12 bg-[var(--color-md-surface-variant)]/50 m-2 rounded-[24px] border border-[var(--color-md-outline)] group-hover:bg-[var(--color-md-surface)] transition-colors duration-500">
                                <UnsavedChatWidget />
                            </div>
                        </motion.div>
                    </FadeIn>

                    {/* Global Search Card */}
                    <FadeIn delay={1.05} className="md:col-span-6">
                        <motion.div
                            whileHover={{ scale: 1.02, y: -5 }}
                            transition={{ type: "spring", stiffness: 300, damping: 20 }}
                            className="group h-full overflow-hidden flex flex-col rounded-[24px] bg-[var(--color-md-surface)] border border-[var(--color-md-outline)] shadow-cinematic-1 hover:shadow-cinematic-2 transition-spring"
                        >
                            <div className="card-oxygen">
                                <div className="w-14 h-14 m3-icon-secondary mb-8">
                                    <Search className="w-7 h-7" />
                                </div>
                                <h3 className="text-3xl font-black text-[var(--color-md-on-surface)] mb-4 tracking-tighter">Search.</h3>
                                <p className="text-base text-[var(--color-md-on-surface-variant)] leading-relaxed">
                                    Find contacts and groups instantly across your entire vault. Precision search for power users.
                                </p>
                            </div>
                            <div className="flex-grow flex items-center justify-center p-6 md:p-12 bg-[var(--color-md-surface-variant)]/50 m-2 rounded-[24px] border border-[var(--color-md-outline)] group-hover:bg-[var(--color-md-surface)] transition-colors duration-500">
                                <SearchWidget />
                            </div>
                        </motion.div>
                    </FadeIn>

                    {/* Screen-Share Safe Mode Card */}
                    <FadeIn delay={1.2} className="md:col-span-6">
                        <motion.div
                            whileHover={{ scale: 1.02, y: -5 }}
                            transition={{ type: "spring", stiffness: 300, damping: 20 }}
                            className="group h-full overflow-hidden flex flex-col rounded-[24px] bg-[var(--color-md-surface)] border border-[var(--color-md-outline)] shadow-cinematic-1 hover:shadow-cinematic-2 transition-spring ring-1 ring-emerald-500/10"
                        >
                            {/* Pure Flat Design: No Background Gradient */}
                            <div className="card-oxygen relative z-10">
                                <div className="w-14 h-14 m3-icon-tertiary mb-8">
                                    <EyeOff className="w-7 h-7" />
                                </div>
                                <h3 className="text-3xl font-black text-[var(--color-md-on-surface)] mb-4 tracking-tighter">Privacy Blur.</h3>
                                <p className="text-base text-[var(--color-md-on-surface-variant)] leading-relaxed mt-2">
                                    Press <kbd className="font-mono text-[11px] font-bold bg-[var(--color-md-surface-variant)] text-[var(--color-md-on-surface)] border border-[var(--color-md-outline-variant)] px-1.5 py-0.5 rounded shadow-sm mx-0.5">⌥ Opt</kbd> <span className="text-[11px] text-[var(--color-md-on-surface-variant)]/60 font-semibold">(Mac)</span> or <kbd className="font-mono text-[11px] font-bold bg-[var(--color-md-surface-variant)] text-[var(--color-md-on-surface)] border border-[var(--color-md-outline-variant)] px-1.5 py-0.5 rounded shadow-sm mx-0.5">Alt</kbd> <span className="text-[11px] text-[var(--color-md-on-surface-variant)]/60 font-semibold">(Win)</span> + <kbd className="font-mono text-[11px] font-bold bg-[var(--color-md-surface-variant)] text-[var(--color-md-on-surface)] border border-[var(--color-md-outline-variant)] px-1.5 py-0.5 rounded shadow-sm mx-0.5">⇧ Shift</kbd> + <kbd className="font-mono text-[11px] font-bold bg-[var(--color-md-surface-variant)] text-[var(--color-md-on-surface)] border border-[var(--color-md-outline-variant)] px-1.5 py-0.5 rounded shadow-sm mx-0.5">P</kbd> to instantly mask names and middle digits. Present with zero PII exposure.
                                </p>
                            </div>
                            <div className="flex-grow flex items-center justify-center p-6 md:p-12 bg-[var(--color-md-surface-variant)]/50 m-2 rounded-[24px] border border-[var(--color-md-outline)] group-hover:bg-[var(--color-md-surface)] transition-colors duration-500">
                                <PrivacyBlurWidget />
                            </div>
                        </motion.div>
                    </FadeIn>
                </div>
            </div>
        </section>
    );
}
